import Sheet from 'components/utils/Sheet'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'
import useMerchandiseCategoriesList from 'hooks/Merchandise/useMerchandiseCategoriesList'
import useMerchandiseCategories from 'hooks/Merchandise/useMerchandiseCategories'
import MerchandiseCategoryMegaMenu from '../MerchandiseMegaMenu/MerchandiseCategoryMenu'
import MerchandiseBrandsMegaMenu from '../MerchandiseMegaMenu/MerchandiseBrandsMenu'
import MerchandiseTravelEssentialsMegaMenu from '../MerchandiseMegaMenu/MerchandiseTravelEssentialsMenu'
import { MerchandiseNavBarButton, MerchandiseNavBarFilterChip } from './MerchandiseNavBarButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import MerchandiseCategoryDrawer from '../MerchandiseMegaMenu/MerchandiseCategoryDrawer'
import MerchandiseBrandsDrawer from '../MerchandiseMegaMenu/MerchandiseBrandsDrawer'
import MerchandiseTravelEssentialsDrawer from '../MerchandiseMegaMenu/MerchandiseTravelEssentialsDrawer'

const Bar = styled(Sheet)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  padding: 0;

  &.disable-sticky {
    position: relative;
  }

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(20)};
  }
`

const ButtonsContainer = styled.div`
  position: relative;
  display: flex;
  gap: ${rem(4)};
`

const ChipsWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: ${rem(48)};
    background: linear-gradient(to left, ${props => props.theme.palette.neutral.default.eight}, transparent);
    pointer-events: none;
  }
`

const ChipsContainer = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding: ${rem(12)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`

function MerchandiseNavBar() {
  const barRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(barRef, 'sticky')
  const showModal = useContext(ModalContext)

  // Categories state
  const [categoriesMenuOpen, setCategoriesMenuOpen] = useState(false)
  const [hoveredCategoryId, setHoveredCategoryId] = useState<string>()
  const [categoryIds] = useMerchandiseCategoriesList({ level: 0, sortBy: 'top' })
  const [categories] = useMerchandiseCategories({
    categoryIds: categoryIds,
  }, { disabled: !categoryIds || categoryIds.length === 0 })
  const firstCategoryId = categories[0]?.id

  // Brands state
  const [_brandsMenuOpen, setBrandsMenuOpen] = useState(false)
  const [selectedLetter, setSelectedLetter] = useState<string>()

  // Travel Essentials state
  const [_travelEssentialsMenuOpen, setTravelEssentialsMenuOpen] = useState(false)

  // Auto-select first category when categories menu opens
  useEffect(() => {
    if (categoriesMenuOpen && !hoveredCategoryId && firstCategoryId) {
      setHoveredCategoryId(firstCategoryId)
    }
  }, [categoriesMenuOpen, hoveredCategoryId, firstCategoryId])

  const handleCategoryHover = useCallback((categoryId: string) => {
    setHoveredCategoryId(categoryId)
  }, [])

  const handleLetterSelect = useCallback((letter: string) => {
    setSelectedLetter(letter)
  }, [])

  const handleCategoriesMenuOpenChange = useCallback((open: boolean) => {
    setCategoriesMenuOpen(open)

    if (!open) {
      setHoveredCategoryId(undefined)
    }
  }, [])

  const handleBrandsMenuOpenChange = useCallback((open: boolean) => {
    setBrandsMenuOpen(open)

    if (!open) {
      setSelectedLetter(undefined)
    }
  }, [])

  const handleTravelEssentialsMenuOpenChange = useCallback((open: boolean) => {
    setTravelEssentialsMenuOpen(open)
  }, [])

  const handleCategoriesChipClick = useCallback(() => {
    showModal(<MerchandiseCategoryDrawer />)
  }, [showModal])

  const handleBrandsChipClick = useCallback(() => {
    showModal(<MerchandiseBrandsDrawer />)
  }, [showModal])

  const handleTravelEssentialsChipClick = useCallback(() => {
    showModal(<MerchandiseTravelEssentialsDrawer />)
  }, [showModal])

  return <Bar background="neutral-eight" ref={barRef}>
    {/* Desktop Navigation */}
    <CSSBreakpoint min="desktop">
      <ButtonsContainer>
        <MerchandiseNavBarButton
          label="Shop by Category"
          onOpenChange={handleCategoriesMenuOpenChange}
        >
          {(menuOpen, onClose) => menuOpen && categories.length > 0 && (
            <MerchandiseCategoryMegaMenu
              categories={categories}
              hoveredCategoryId={hoveredCategoryId}
              onCategoryHover={handleCategoryHover}
              onClose={onClose}
            />
          )}
        </MerchandiseNavBarButton>

        <MerchandiseNavBarButton
          label="Shop by Brand"
          onOpenChange={handleBrandsMenuOpenChange}
        >
          {(menuOpen, onClose) => menuOpen && (
            <MerchandiseBrandsMegaMenu
              selectedLetter={selectedLetter}
              onLetterSelect={handleLetterSelect}
              onClose={onClose}
            />
          )}
        </MerchandiseNavBarButton>

        <MerchandiseNavBarButton
          label="Travel Essentials"
          onOpenChange={handleTravelEssentialsMenuOpenChange}
          alignDropdownWithButton
        >
          {(menuOpen, onClose) => menuOpen && (
            <MerchandiseTravelEssentialsMegaMenu
              onClose={onClose}
            />
          )}
        </MerchandiseNavBarButton>

        <MerchandiseNavBarButton
          label="Earn Triple Points"
          to="/au/search/shop?tags=triplepoints"
          showChevron={false}
        />
      </ButtonsContainer>
    </CSSBreakpoint>

    {/* Mobile/Tablet Navigation */}
    <CSSBreakpoint max="tablet">
      <ChipsWrapper>
        <ChipsContainer>
          <MerchandiseNavBarFilterChip
            label="Categories"
            showChevron
            onClick={handleCategoriesChipClick}
          />
          <MerchandiseNavBarFilterChip
            label="Brands"
            showChevron
            onClick={handleBrandsChipClick}
          />
          <MerchandiseNavBarFilterChip
            label="Travel Essentials"
            showChevron
            onClick={handleTravelEssentialsChipClick}
          />
          <MerchandiseNavBarFilterChip
            label="Earn Triple Points"
            to="/au/search/shop?tags=triplepoints"
          />
        </ChipsContainer>
      </ChipsWrapper>
    </CSSBreakpoint>
  </Bar>
}

export default MerchandiseNavBar
